/**
 * useVisitClock — single source of truth for "what timestamp do I use for the
 * next visit event?" (#1863)
 *
 * Live mode (no ManualVisitContext set): every call returns new Date().
 *
 * Manual mode (ManualVisitContext set by ManualEntryPreflight): each visit
 * event returns the appropriate slot from the manual data. The rep entered
 * those times in the pre-flight; we replay them through the canonical
 * StoreDetail workflow so the UI is identical to a live visit.
 *
 * Event vocabulary:
 *   - `now`           generic "right now" - used for things that should always
 *                     be the actual clock even in manual mode (animation ticks,
 *                     IDB record createdAt, telemetry timestamps).
 *   - `today_date`    the rep's "today" for filtering / fetching. In manual
 *                     mode this is the visit date (YYYY-MM-DD), so historical
 *                     CallLogs and timeline data load for the right day.
 *   - `arrival`       store check-in time. Live: now. Manual: timeIn.
 *   - `task_start(i)` start time for task i in the visit. Live: now. Manual:
 *                     tasks[i].startTime (or timeIn for single-task visits).
 *   - `task_end(i)`   end time for task i. Live: now. Manual: tasks[i].endTime
 *                     (or timeOut for single-task visits).
 *   - `departure`     check-out (leave store) time. Live: now. Manual: timeOut.
 *   - `drive_to_start` / `drive_to_end` - drive-segment endpoints. Live: now.
 *                     Manual: driveStartTime / driveEndTime.
 *
 * Each accessor returns a Date for symmetry with `new Date()`. Helpers below
 * convert to ISO / HH:MM as needed by call sites.
 */

import { useMemo } from 'react';
import { useManualVisitData } from '../context/ManualVisitContext';
import { formatDateToLocal } from '../utils/timesheetRules';

export interface VisitClock {
  /** Always returns the real current time (manual mode does NOT override). */
  now: () => Date;

  /** The "today" date string (YYYY-MM-DD) the UI should operate on.
   *  Live: real today. Manual: the visit's date. */
  todayDate: () => string;

  /** Whether we're in manual (injected-time) mode. */
  isManual: boolean;

  /** Store arrival timestamp for check-in event. Live: now. Manual: timeIn. */
  arrival: () => Date;

  /** Start of a task in the visit. Live: now. Manual: tasks[i].startTime,
   * falling back to the visit timeIn for single-task visits. */
  taskStart: (taskIndex?: number) => Date;

  /** End of a task in the visit. Live: now. Manual: tasks[i].endTime,
   * falling back to the visit timeOut for single-task visits. */
  taskEnd: (taskIndex?: number) => Date;

  /** Store departure / check-out timestamp. Live: now. Manual: timeOut. */
  departure: () => Date;

  /** Drive-segment start (when the rep left home / previous stop). */
  driveToStart: () => Date;

  /** Drive-segment end (== arrival at the store). */
  driveToEnd: () => Date;

  /** Convenience: HH:MM string for a given visit-clock event, useful for
   * call sites that previously did `new Date().toTimeString().slice(0, 5)`. */
  toHHMM: (d: Date) => string;
}

function toHHMM(d: Date): string {
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

export function useVisitClock(): VisitClock {
  const manual = useManualVisitData();

  return useMemo<VisitClock>(() => {
    const isManual = manual !== null;

    // #2465: never return an Invalid Date. A malformed manual timeIn/timeOut/slot
    // time used to flow through as `new Date(bad)` → Invalid Date → a downstream
    // session end stored as the literal "Invalid Date" (the checkout summary's
    // "12:30 PM - Invalid Date / 0s"). Parse defensively, falling back to a valid
    // value so the time is at worst wrong-but-editable, never invalid.
    const safe = (value: string | null | undefined, fallback: Date): Date => {
      if (!value) return fallback;
      const d = new Date(value);
      return isNaN(d.getTime()) ? fallback : d;
    };

    return {
      isManual,
      now: () => new Date(),
      todayDate: () => (manual ? manual.date : formatDateToLocal(new Date())),

      arrival: () => (manual ? safe(manual.timeIn, new Date()) : new Date()),

      taskStart: (taskIndex = 0) => {
        if (!manual) return new Date();
        const slot = manual.tasks?.[taskIndex];
        return safe(slot ? slot.startTime : manual.timeIn, new Date());
      },

      taskEnd: (taskIndex = 0) => {
        if (!manual) return new Date();
        const slot = manual.tasks?.[taskIndex];
        // Fall back to this task's START if the end is missing/malformed — a 0-min
        // task is honest and editable; an Invalid Date is neither.
        const start = safe(slot ? slot.startTime : manual.timeIn, new Date());
        return safe(slot ? slot.endTime : manual.timeOut, start);
      },

      departure: () => (manual ? safe(manual.timeOut, new Date()) : new Date()),

      driveToStart: () => (manual ? safe(manual.driveStartTime, new Date()) : new Date()),
      driveToEnd: () => (manual ? safe(manual.driveEndTime, new Date()) : new Date()),

      toHHMM,
    };
  }, [manual]);
}
